/**
 * Historical Sentiment Service
 *
 * Calculates group and market sentiment over time (daily data points)
 * Enables historical charts, accuracy tracking, and trend analysis
 */

import { supabase } from '@/services/supabase/supabaseClient';
import { marketSentimentService } from '@/services/market/marketSentimentService';
import {
  HistoricalSentimentData,
  SentimentDataPoint,
  EdgeAnalysis,
  HistoricalSentimentResponse,
} from '@/types/historical-sentiment.types';

class HistoricalSentimentService {
  /**
   * Get historical sentiment data for a group (last N days)
   * Reads from daily_group_sentiment table for fast performance
   */
  async getHistoricalSentiment(
    groupId: string,
    days: number = 7
  ): Promise<HistoricalSentimentResponse> {
    try {
      console.log(`[HistoricalSentiment] Fetching ${days} days of data for group:`, groupId);

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - (days - 1)); // Include today

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      // Fetch data from daily_group_sentiment table
      const { data: snapshots, error: snapshotsError } = await supabase
        .from('daily_group_sentiment')
        .select('*')
        .eq('group_id', groupId)
        .gte('date', startDateStr)
        .lte('date', endDateStr)
        .order('date', { ascending: true });

      if (snapshotsError) {
        console.error('[HistoricalSentiment] Error fetching snapshots:', snapshotsError);
        return { data: null, error: 'Failed to fetch historical data' };
      }

      // Convert database rows to SentimentDataPoint
      const dataPoints: SentimentDataPoint[] = (snapshots || []).map(snapshot => ({
        date: snapshot.date,
        groupSentiment: snapshot.group_sentiment,
        marketSentiment: snapshot.market_sentiment,
        edge: snapshot.edge,
        messageCount: snapshot.message_count,
      }));

      // If no data found, return empty dataset
      if (dataPoints.length === 0) {
        console.log('[HistoricalSentiment] No historical data found');
        return {
          data: {
            groupId,
            dataPoints: [],
            startDate: startDateStr,
            endDate: endDateStr,
            averageEdge: null,
            accuracy: null,
            lastUpdated: new Date().toISOString(),
          },
          error: null,
        };
      }

      // Calculate aggregate metrics
      const averageEdge = this.calculateAverageEdge(dataPoints);
      const accuracy = null; // TODO: Implement accuracy calculation

      const historicalData: HistoricalSentimentData = {
        groupId,
        dataPoints,
        startDate: startDateStr,
        endDate: endDateStr,
        averageEdge,
        accuracy,
        lastUpdated: new Date().toISOString(),
      };

      console.log('[HistoricalSentiment] Found', dataPoints.length, 'data points');
      return { data: historicalData, error: null };
    } catch (error) {
      console.error('[HistoricalSentiment] Error:', error);
      return { data: null, error: 'Failed to fetch historical sentiment' };
    }
  }

  /**
   * Calculate average edge over time period
   */
  private calculateAverageEdge(dataPoints: SentimentDataPoint[]): number | null {
    const edgesWithData = dataPoints.filter(dp => dp.edge !== null).map(dp => dp.edge as number);

    if (edgesWithData.length === 0) return null;

    const sum = edgesWithData.reduce((acc, edge) => acc + edge, 0);
    return Math.round(sum / edgesWithData.length);
  }

  /**
   * Analyze edge trends (max, min, volatility)
   */
  analyzeEdge(dataPoints: SentimentDataPoint[]): EdgeAnalysis {
    const edgesWithData = dataPoints.filter(dp => dp.edge !== null);

    if (edgesWithData.length === 0) {
      return {
        maxEdge: null,
        minEdge: null,
        maxEdgeDate: null,
        minEdgeDate: null,
        averageEdge: null,
        volatility: 0,
      };
    }

    const edges = edgesWithData.map(dp => dp.edge as number);
    const maxEdge = Math.max(...edges);
    const minEdge = Math.min(...edges);
    const maxEdgePoint = edgesWithData.find(dp => dp.edge === maxEdge);
    const minEdgePoint = edgesWithData.find(dp => dp.edge === minEdge);

    const averageEdge = this.calculateAverageEdge(dataPoints);

    // Calculate volatility (standard deviation)
    const mean = averageEdge || 0;
    const squaredDiffs = edges.map(edge => Math.pow(edge - mean, 2));
    const variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / edges.length;
    const volatility = Math.round(Math.sqrt(variance));

    return {
      maxEdge,
      minEdge,
      maxEdgeDate: maxEdgePoint?.date || null,
      minEdgeDate: minEdgePoint?.date || null,
      averageEdge,
      volatility,
    };
  }

  /**
   * Clear cache (for testing or manual refresh)
   */
  clearCache(): void {
    console.log('[HistoricalSentiment] Cache cleared');
  }
}

export const historicalSentimentService = new HistoricalSentimentService();
